/**
 * Centralized plan differentials and badges for all subscription plans
 * Used by both sponsor and sponsored roles
 */

export interface PlanDifferentialsData {
  topDifferentials: string[];
  fullBenefits: string[];
  miniSummary: string;
}

export interface PlanBadge {
  type: 'recommended' | 'value' | 'urgency';
  text: string;
  className: string;
}

/**
 * Top 3 differentials + full benefits per plan
 */
export const PLAN_DIFFERENTIALS: Record<string, PlanDifferentialsData> = {
  // Sponsor plans
  sponsor_premium: {
    topDifferentials: [
      "Conexões ilimitadas todos os dias",
      "Apareça primeiro para perfis novos",
      "Destaque-se no feed principal"
    ],
    fullBenefits: [
      "Conversas ilimitadas por dia",
      "Visualizações de perfil ilimitadas",
      "Filtros avançados de busca",
      "Filtro exclusivo de verificados",
      "Boosts mensais inclusos",
      "Destaque permanente no feed",
      "Prioridade no atendimento"
    ],
    miniSummary: "Ilimitado • Filtros avançados • Destaque no feed"
  },
  sponsor_standard: {
    topDifferentials: [
      "Encontre até 30 conexões por dia",
      "Descubra pessoas compatíveis",
      "Seja visto por mais perfis"
    ],
    fullBenefits: [
      "30 novas conversas por dia",
      "Visualizações de perfil ilimitadas",
      "Filtros avançados de busca",
      "Confirmações de leitura",
      "Visibilidade aumentada"
    ],
    miniSummary: "30 conexões/dia • Filtros avançados • Visibilidade+"
  },
  sponsor_free: {
    topDifferentials: [],
    fullBenefits: [
      "5 mensagens por dia",
      "10 conexões por mês",
      "20 visualizações de perfil por dia",
      "Funcionalidades básicas"
    ],
    miniSummary: "Limitado"
  },

  // Sponsored plans
  sponsored_premium: {
    topDifferentials: [
      "Destaque permanente no feed",
      "Filtros avançados disponíveis",
      "Visibilidade prioritária"
    ],
    fullBenefits: [
      "Conversas ilimitadas",
      "Visualizações de perfil ilimitadas",
      "Filtros avançados de busca",
      "Filtro exclusivo de verificados",
      "Destaque permanente no feed",
      "Prioridade no atendimento",
      "Visibilidade máxima"
    ],
    miniSummary: "Ilimitado • Filtros avançados • Destaque no feed"
  },
  sponsored_free: {
    topDifferentials: [],
    fullBenefits: [
      "Conversas ilimitadas",
      "Visualizações de perfil ilimitadas",
      "Funcionalidades básicas"
    ],
    miniSummary: "Limitado"
  }
};

/**
 * Badge configuration per plan
 */
export const PLAN_BADGES: Record<string, PlanBadge | null> = {
  sponsor_premium: {
    type: 'recommended',
    text: '⭐ RECOMENDADO',
    className: 'bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30'
  },
  sponsored_premium: {
    type: 'recommended',
    text: '⭐ RECOMENDADO',
    className: 'bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30'
  },
  sponsor_standard: null,
  sponsor_free: null,
  sponsored_free: null
};

/**
 * Card styling variants per plan type
 */
export const getCardClasses = (planId: string): string => {
  if (planId.includes('free')) {
    return "bg-white/10 border-white/20"; // Free plans: lighter
  }
  if (planId.includes('premium')) {
    return "bg-luxury-pinkBright/5 border-luxury-pinkBright/30"; // Premium: vivid
  }
  return "bg-white/5 border-white/10"; // Standard: default
};
